<template>
	<div class="fade">
		<nuxt-link :to="articleHref" class="article-teaser">
			<div class="overflow-hidden h-[280px] lg:h-auto mb-4">
				<nuxt-img
					v-if="articleContent.image"
					:src="`${articleContent.image}`"
					provider="storyblok"
					loading="lazy"
					format="webp"
					width="800"
					height="600"
				/>
			</div>
		</nuxt-link>
		<div class="flex flex-wrap gap-1 items-center font-semibold text-sm">
			<nuxt-link
				v-for="category in categs"
				:key="category.uuid"
				:to="categoryHref(category.slug)"
				class="flex items-center gap-1"
			>
				{{ category.content.category_name }}
			</nuxt-link>
		</div>
		<ArticlePublicationDates
			:updated="articleContent.updated_at"
			:first-published="articleContent.first_published_at"
			:fallback-date="articleContent.date"
			class="text-sm mb-2"
		/>
		<nuxt-link :to="articleHref">
			<h2>
				{{ articleContent.name }}
			</h2>
			<p class="leading-relaxed">
				{{ articleContent.intro }}
			</p>
		</nuxt-link>
	</div>
</template>

<script setup lang="ts">
import type { BlokArticleContent } from "~/types/storyblok/storyblok";
import { normalizeStoryblokSlug } from "~/utils/storyblok/resolveStoryblokSlug";

const localePath = useLocalePath();
const { stripFolder } = useBlogPath();

interface Props {
	articleContent: BlokArticleContent;
	articleLink: string;
	articleCategories: {
		uuid: string;
		slug: string;
		content: { category_name: string };
	}[];
}
const { articleContent, articleCategories, articleLink } = defineProps<Props>();

// Strip whichever site-folder prefix is in the slug (active or
// fallback) — for locales with `isLocaleBlog: false` the article
// comes from the default site, so the slug carries the fallback
// folder, not the active one.
const articleSlug = computed(() =>
	stripFolder(normalizeStoryblokSlug(articleLink))
);
const blogRoot = computed(() => articleSlug.value.split("/")[0] ?? "");
const articleCategorySlug = computed(
	() => articleSlug.value.split("/")[1] ?? ""
);

const articleHref = computed(() => localePath(`/${articleSlug.value}`));
const categoryHref = (categorySlug: string) =>
	localePath(`/${blogRoot.value}/${categorySlug}`);

const categs = ref<Props["articleCategories"]>([]);
const filterCategories = () => {
	const categorySlug = articleCategorySlug.value;
	if (!categorySlug) return [];
	return articleCategories.filter((category) => category.slug === categorySlug);
};
categs.value = filterCategories();
</script>

<style>
.fade a .image-container img {
	transition: all 0.2s linear;
	object-fit: contain;
}
.fade:hover img {
	transform: scale(1.2);
}
@media (max-width: 1024px) {
	.fade a .image-container {
		height: 100%;
	}
	.fade a .image-container img {
		max-width: 100% !important;
		width: 100%;
	}
}
</style>
